import {
  num,
  pct,
  safeDiv,
  str,
  type CalculatorDefinition,
  type CalculatorOutput,
  type CalculatorValues,
} from "./types";

/**
 * Micro-business valuation on an SDE multiple.
 *
 * Businesses under roughly $1M of earnings trade on Seller's Discretionary
 * Earnings — profit with the owner's own compensation and one-off costs added
 * back — not EBITDA. The multiple is then moved by how transferable the
 * business is without its current owner.
 */

/** Typical SDE multiple bands by business type. */
const BASE_MULTIPLES: Record<string, { low: number; mid: number; high: number }> = {
  "local-cashflow": { low: 1.8, mid: 2.5, high: 3.2 },
  "micro-saas": { low: 2.6, mid: 3.6, high: 5.0 },
  "digital-asset": { low: 1.8, mid: 2.6, high: 3.4 },
  "service-business": { low: 1.6, mid: 2.2, high: 3.0 },
};

/**
 * Quality adjustments, in multiple points added to the base.
 *
 * These are directional rules of thumb from how small-business brokers price
 * transferability — not appraisal standards.
 */
const OWNER_DEPENDENCE_ADJ: Record<string, number> = {
  absentee: 0.6, // runs without the owner
  light: 0.25, // owner oversees, does not operate
  operator: -0.15, // owner works in it daily
  critical: -0.7, // the owner *is* the business
};

const GROWTH_ADJ: Record<string, number> = {
  declining: -0.7,
  flat: -0.1,
  growing: 0.3,
  fast: 0.65,
};

function isSaaS(values: CalculatorValues) {
  return str(values, "businessType") === "micro-saas";
}

export const valuationCalculator: CalculatorDefinition = {
  kind: "business-valuation",
  slug: "micro-business-valuation",
  name: "Micro-Business Valuation",
  nameKo: "소규모 사업 가치 평가",
  summary:
    "Price a small business on an SDE multiple, adjusted for how much of it walks out the door with the current owner.",
  interpretation:
    "Under about $1M of earnings, buyers price Seller's Discretionary Earnings — profit plus owner pay plus genuine one-offs — at a multiple set by transferability. The single largest swing is owner dependence: the same earnings are worth roughly twice as much when the business runs without the person selling it. Treat the output as a negotiating range, never a number.",
  disclaimer:
    "Educational estimate using broad market rules of thumb. Not an appraisal, a valuation opinion, or investment advice. A real transaction needs diligence on verified financials.",
  relatedCaseStudySlugs: ["micro-saas-invoice-tool", "laundromat-seller-financed"],

  fields: [
    {
      id: "businessType",
      label: "Business type",
      kind: "select",
      group: "Business",
      defaultValue: "local-cashflow",
      options: [
        { value: "local-cashflow", label: "Local cash-flow business" },
        { value: "micro-saas", label: "Micro-SaaS / software" },
        { value: "digital-asset", label: "Digital asset / content" },
        { value: "service-business", label: "Service business" },
      ],
      help: "Sets the starting multiple band. Software trades higher because it scales without headcount.",
    },
    {
      id: "annualRevenue",
      label: "Annual revenue",
      kind: "currency",
      group: "Earnings",
      defaultValue: 240000,
      min: 0,
      step: 5000,
    },
    {
      id: "annualExpenses",
      label: "Annual expenses",
      kind: "currency",
      group: "Earnings",
      defaultValue: 168000,
      min: 0,
      step: 5000,
      help: "All operating costs as filed, including the owner's own compensation.",
    },
    {
      id: "ownerCompensation",
      label: "Owner compensation in expenses",
      kind: "currency",
      group: "Earnings",
      defaultValue: 45000,
      min: 0,
      step: 1000,
      help: "Added back, because the buyer gets to decide their own salary.",
    },
    {
      id: "addbacks",
      label: "One-time & personal add-backs",
      kind: "currency",
      group: "Earnings",
      defaultValue: 8000,
      min: 0,
      step: 500,
      help: "Genuine non-recurring costs only. Inflating this line is the most common way sellers overprice.",
    },
    {
      id: "replacementManagerCost",
      label: "Cost to replace the owner",
      kind: "currency",
      group: "Earnings",
      defaultValue: 0,
      min: 0,
      step: 1000,
      help: "What you would pay a manager to do the owner's job. Subtracted, because a buyer who will not work in the business must hire this out.",
    },

    /* ------------------------------- Quality ------------------------------ */
    {
      id: "ownerDependence",
      label: "Owner dependence",
      kind: "select",
      group: "Quality",
      defaultValue: "operator",
      options: [
        { value: "absentee", label: "Absentee — runs without the owner" },
        { value: "light", label: "Light — owner oversees only" },
        { value: "operator", label: "Operator — owner works in it daily" },
        { value: "critical", label: "Critical — the owner is the business" },
      ],
    },
    {
      id: "growthTrend",
      label: "Revenue trend",
      kind: "select",
      group: "Quality",
      defaultValue: "flat",
      options: [
        { value: "declining", label: "Declining" },
        { value: "flat", label: "Flat" },
        { value: "growing", label: "Growing steadily" },
        { value: "fast", label: "Growing fast" },
      ],
    },
    {
      id: "recurringRevenuePct",
      label: "Recurring revenue",
      kind: "percent",
      group: "Quality",
      defaultValue: 20,
      min: 0,
      max: 100,
      step: 5,
      help: "Share under contract or subscription. Predictable revenue is worth more than the same dollars won fresh each month.",
    },
    {
      id: "customerConcentrationPct",
      label: "Largest customer share",
      kind: "percent",
      group: "Quality",
      defaultValue: 10,
      min: 0,
      max: 100,
      step: 5,
      help: "Revenue from the single biggest customer. Above 25% and buyers start discounting hard.",
    },
    {
      id: "monthlyChurnPct",
      label: "Monthly churn",
      kind: "percent",
      group: "Quality",
      defaultValue: 3,
      min: 0,
      max: 100,
      step: 0.25,
      visibleWhen: isSaaS,
      help: "Share of subscribers lost per month.",
    },

    /* -------------------------------- Assets ------------------------------ */
    {
      id: "inventoryValue",
      label: "Inventory & equipment included",
      kind: "currency",
      group: "Assets",
      defaultValue: 0,
      min: 0,
      step: 1000,
      help: "Added on top of the earnings multiple when transferred at close.",
    },
    {
      id: "debtAssumed",
      label: "Debt assumed by buyer",
      kind: "currency",
      group: "Assets",
      defaultValue: 0,
      min: 0,
      step: 1000,
    },
  ],

  compute: (values) => {
    const type = str(values, "businessType", "local-cashflow");
    const revenue = num(values, "annualRevenue");
    const expenses = num(values, "annualExpenses");
    const ownerComp = num(values, "ownerCompensation");
    const addbacks = num(values, "addbacks");
    const replacementCost = num(values, "replacementManagerCost");
    const inventory = num(values, "inventoryValue");
    const debt = num(values, "debtAssumed");

    const recurring = pct(values, "recurringRevenuePct");
    const concentration = pct(values, "customerConcentrationPct");
    const churn = pct(values, "monthlyChurnPct");

    const netProfit = revenue - expenses;
    const sde = netProfit + ownerComp + addbacks;
    /** What a hands-off buyer actually acquires. */
    const adjustedSde = sde - replacementCost;

    const band = BASE_MULTIPLES[type] ?? BASE_MULTIPLES["local-cashflow"];

    let adjustment = 0;
    adjustment += OWNER_DEPENDENCE_ADJ[str(values, "ownerDependence", "operator")] ?? 0;
    adjustment += GROWTH_ADJ[str(values, "growthTrend", "flat")] ?? 0;
    // Recurring revenue is worth up to +0.5× at 100% contracted.
    adjustment += recurring * 0.5;
    // Concentration above 25% is penalised, scaling to -0.8× at total reliance.
    if (concentration > 0.25) {
      adjustment -= ((concentration - 0.25) / 0.75) * 0.8;
    }
    // SaaS churn: 2%/mo is par; every point above costs a fifth of a turn.
    if (type === "micro-saas" && churn > 0.02) {
      adjustment -= Math.min((churn - 0.02) * 100 * 0.2, 1.5);
    }

    const clampMultiple = (m: number) => Math.max(m + adjustment, 0.4);
    const lowMultiple = clampMultiple(band.low);
    const midMultiple = clampMultiple(band.mid);
    const highMultiple = clampMultiple(band.high);

    const equityValue = (multiple: number) =>
      Math.max(adjustedSde * multiple + inventory - debt, 0);

    const lowValue = equityValue(lowMultiple);
    const midValue = equityValue(midMultiple);
    const highValue = equityValue(highMultiple);

    const sdeMargin = safeDiv(sde, revenue);
    const revenueMultiple = safeDiv(midValue, revenue);

    const outputs: CalculatorOutput[] = [
      {
        id: "midValue",
        label: "Indicative value",
        value: midValue,
        format: "currency",
        tone: "currency",
        primary: true,
        note: `At ${midMultiple.toFixed(2)}× adjusted SDE.`,
      },
      {
        id: "lowValue",
        label: "Low end of range",
        value: lowValue,
        format: "currency",
        tone: "neutral",
        primary: true,
        note: `${lowMultiple.toFixed(2)}× — a buyer with leverage.`,
      },
      {
        id: "highValue",
        label: "High end of range",
        value: highValue,
        format: "currency",
        tone: "neutral",
        primary: true,
        note: `${highMultiple.toFixed(2)}× — a clean, well-documented sale.`,
      },
      {
        id: "sde",
        label: "Seller's discretionary earnings",
        value: sde,
        format: "currency",
        tone: sde >= 0 ? "currency" : "risk",
        note: "Net profit plus owner compensation plus add-backs.",
      },
      {
        id: "adjustedSde",
        label: "SDE after manager replacement",
        value: adjustedSde,
        format: "currency",
        tone: adjustedSde >= 0 ? "currency" : "risk",
        note: "The earnings a hands-off buyer actually inherits.",
      },
      {
        id: "netProfit",
        label: "Net profit as filed",
        value: netProfit,
        format: "currency",
        tone: netProfit >= 0 ? "currency" : "risk",
      },
      {
        id: "appliedMultiple",
        label: "Applied multiple",
        value: midMultiple,
        format: "multiple",
        tone: "neutral",
        note: `Base ${band.mid.toFixed(1)}× with a ${adjustment >= 0 ? "+" : ""}${adjustment.toFixed(2)} quality adjustment.`,
      },
      {
        id: "sdeMargin",
        label: "SDE margin",
        value: (sdeMargin ?? 0) * 100,
        format: "percent",
        tone: "neutral",
        note: "Earnings as a share of revenue.",
      },
      {
        id: "revenueMultiple",
        label: "Implied revenue multiple",
        value: revenueMultiple ?? 0,
        format: "multiple",
        tone: "neutral",
        note: "Useful as a sanity check against comparable listings.",
      },
    ];

    const warnings: string[] = [];
    if (sde <= 0) {
      warnings.push(
        "SDE is zero or negative. A business with no earnings is priced on its assets, not a multiple.",
      );
    }
    if (adjustedSde < sde * 0.5 && replacementCost > 0) {
      warnings.push(
        "Replacing the owner consumes over half the earnings. This is a job being sold as an asset.",
      );
    }
    if (concentration > 0.4) {
      warnings.push(
        "One customer is over 40% of revenue. Confirm that contract survives a change of ownership before pricing anything.",
      );
    }
    if (addbacks > sde * 0.35 && sde > 0) {
      warnings.push(
        "Add-backs exceed a third of SDE. Every one needs a receipt — this is where asking prices inflate.",
      );
    }
    if (type === "micro-saas" && churn > 0.05) {
      warnings.push(
        "Monthly churn above 5% means the customer base turns over inside two years. The multiple reflects that, harshly.",
      );
    }

    return { outputs, warnings };
  },
};
